import { useMemo, useState } from 'react'
import Icon from '../Icon'
import { api, go, useContent, useLive, type Inbound, type LiveState, type Me, type RunInfo, type Tally } from '../../lib/classroom'
import { L, w } from '../../lib/words'
import { sound } from '../../lib/fun'
import { correctText, isAnswerable, type Pool, type Result, type Step } from '../../../shared/classroom'
import { lang } from '../../lib/words'
import { StepBody } from './Step'
import { Board } from './LiveStudent'
import Pichi, { EndBird } from './Pichi'
import { Sheet } from './Sheets'

const GROUPS: { key: 'right' | 'wrong' | 'idk' | 'none'; label: () => string; match: (r: Result | undefined) => boolean }[] = [
  { key: 'right', label: () => w('right'), match: (r) => r === 'right' || r === 'close' },
  { key: 'wrong', label: () => w('wrong'), match: (r) => r === 'wrong' },
  { key: 'idk', label: () => w('idk'), match: (r) => r === 'idk' },
  { key: 'none', label: () => w('none'), match: (r) => r === undefined },
]

/**
 * The teacher's console. Drive the lesson, watch every answer arrive, and call
 * on somebody — at random from the right, wrong, "No sé" or silent group, or
 * by tapping a name. The "needs a reteach" list (three "No sé" in a row) sits
 * beside it with a button to send that student a private review.
 */
export default function LiveTeacher({ runId, room, onEnded }: { runId: string; room: Me; onEnded?: () => void }) {
  const modules = useContent()
  const [state, setState] = useState<LiveState | null>(null)
  const [tally, setTally] = useState<Tally | null>(null)
  const [empty, setEmpty] = useState<Pool | null>(null)
  const [ended, setEnded] = useState<RunInfo['board'] | null>(null)
  const [sent, setSent] = useState<Record<string, boolean>>({})
  const [ending, setEnding] = useState(false)

  const onMessage = (m: Inbound) => {
    if (m.t === 'state') { setState(m); if (m.ended) onEnded?.() }
    else if (m.t === 'tally') setTally(m)
    else if (m.t === 'callon-empty') { setEmpty(m.pool); setTimeout(() => setEmpty(null), 2500) }
    else if (m.t === 'ended') { onEnded?.(); setEnded(m.summary?.board ?? []) }
  }
  const { status, send } = useLive(runId, onMessage)

  const lesson = useMemo(() => {
    for (const m of modules ?? []) for (const l of m.lessons) if (l.id === state?.lessonId) return l
    return null
  }, [modules, state?.lessonId])

  if (ended || state?.ended) {
    return (
      <div className="card done-screen">
        {/* The same bird the students see at the end. */}
        <EndBird level={room.level.index} look={room.look} />
        <h1>{w('lessonOver')}</h1>
        {ended && <Board board={ended} />}
        <button className="primary" onClick={() => go('/')}>{w('home')}</button>
      </div>
    )
  }
  if (!state || !lesson) return <div className="boot">{status === 'reconnecting' ? w('reconnecting') : w('connecting')}</div>

  const step = lesson.steps[state.step] as Step
  const answerable = isAnswerable(step)
  const answers = tally?.answers ?? {}
  const present = tally?.present ?? []
  const nameOf = (id: string) => present.find((p) => p.id === id)?.name ?? '—'

  const goto = (i: number) => send({ t: 'goto', index: i })
  const callOn = (pool: Pool, userId?: string) => { sound('callon'); send({ t: 'callon', pool, userId }) }

  // Bars: per choice for choice/poll/yesno; per result for guesses.
  const bars: { label: string; n: number; good?: boolean }[] = []
  if (step.type === 'question' && step.kind === 'yesno') {
    for (const v of [true, false]) {
      bars.push({ label: v ? w('yes') : w('no'), n: Object.values(answers).filter((a) => 'value' in a.value && a.value.value === v).length, good: step.answer === v })
    }
  } else if ((step.type === 'question' && step.kind === 'choice') || step.type === 'poll') {
    step.choices.forEach((c, i) => bars.push({
      label: L(c), n: Object.values(answers).filter((a) => 'value' in a.value && a.value.value === i).length,
      good: step.type === 'question' ? step.answer === i : undefined,
    }))
  }
  if (step.type === 'question') bars.push({ label: w('idk'), n: Object.values(answers).filter((a) => 'idk' in a.value).length })
  const max = Math.max(1, ...bars.map((b) => b.n))

  return (
    <div className="live-teacher">
      <header className="console-top">
        <div>
          <strong>{L(lesson.title)}</strong>
          <span className="muted small"> · {state.present} {w('connected')}{answerable ? ` · ${state.answered} ${w('answered')}` : ''}</span>
          {status !== 'open' && <span className="warn small"> {w('reconnecting')}</span>}
        </div>
        <div className="row">
          <button onClick={() => window.open(`/present/${runId}`, 'adulting-present')}><Icon name="screen" /> {w('presentScreen')}</button>
          <button className="danger" onClick={() => setEnding(true)}>{w('endLesson')}</button>
        </div>
      </header>

      <div className="scrubber" role="tablist">
        {lesson.steps.map((s, i) => (
          <button key={s.id} className={`dot dot-${s.type}${i === state.step ? ' on' : ''}`} onClick={() => goto(i)} title={`${i + 1}`} aria-label={`${i + 1}`} />
        ))}
      </div>

      <div className="console-grid">
        <section className="card">
          <StepBody step={step} teacher />
          {step.type === 'question' && (
            <p className="small"><Icon name="check" size={16} /> <strong>{correctText(step, lang())}</strong></p>
          )}
          <div className="row nav-row">
            <button disabled={state.step === 0} onClick={() => goto(state.step - 1)}><Icon name="back" /> {w('prev')}</button>
            {answerable && (state.open
              ? <button onClick={() => send({ t: 'close' })}><Icon name="lock" /> {w('close')}</button>
              : <button onClick={() => send({ t: 'open' })}><Icon name="bolt" /> {w('open')}</button>)}
            {answerable && <button onClick={() => send({ t: 'reveal' })} aria-pressed={state.reveal}><Icon name="eye" /> {w('reveal')}</button>}
            {state.step >= state.total - 1
              ? <button className="narrow danger-fill" onClick={() => setEnding(true)}><Icon name="check" /> {w('endLesson')}</button>
              : <button className="primary narrow" onClick={() => goto(state.step + 1)}>{w('next')} <Icon name="chevron" /></button>}
          </div>
        </section>

        <section className="card">
          {state.callOn && (
            <div className="called">
              <Icon name="hand" size={28} />
              <span><span className="muted small">{w('called')}</span><br /><strong className="called-name">{state.callOn.name}</strong></span>
              <button onClick={() => send({ t: 'dismiss' })}>{w('dismiss')}</button>
            </div>
          )}

          {answerable && bars.length > 0 && (
            <div className="bars">
              {bars.map((b, i) => (
                <div key={i} className={`bar-row${b.good ? ' good' : ''}`}>
                  <span className="bar-label">{b.label}</span>
                  <span className="bar-track"><span style={{ width: `${(b.n / max) * 100}%` }} /></span>
                  <span className="bar-n">{b.n}</span>
                </div>
              ))}
            </div>
          )}

          <div className="callon-row">
            <span className="small muted"><Icon name="dice" size={16} /> {w('random')}:</span>
            {step.type === 'question' && GROUPS.filter((g) => g.key !== 'wrong').map((g) => (
              <button key={g.key} className={`pool pool-${g.key}`} onClick={() => callOn(g.key)}>{g.label()}</button>
            ))}
            {step.type === 'poll' && <button className="pool" onClick={() => callOn('none')}>{w('none')}</button>}
            <button className="pool" onClick={() => callOn('any')}>{w('any')}</button>
          </div>
          {empty && <p className="muted small">{w('nobodyInPool')}</p>}

          {step.type === 'poll' && (
            <div className="groups">
              {[...step.choices.map((c, i) => ({ key: `c${i}`, label: L(c), ids: present.map((p) => p.id).filter((id) => { const a = answers[id]?.value; return !!a && 'value' in a && a.value === i }) })),
                { key: 'none', label: w('none'), ids: present.map((p) => p.id).filter((id) => !answers[id]) }].map((g) => (
                <div key={g.key} className={`group group-${g.key}`}>
                  <h4>{g.label} <span>{g.ids.length}</span></h4>
                  <div className="names">
                    {g.ids.map((id) => <button key={id} className="name" onClick={() => callOn('any', id)}>{nameOf(id)}</button>)}
                  </div>
                </div>
              ))}
            </div>
          )}
          {step.type === 'question' && (
            <div className="groups">
              {GROUPS.map((g) => {
                const ids = present.map((p) => p.id).filter((id) => g.match(answers[id]?.result))
                return (
                  <div key={g.key} className={`group group-${g.key}`}>
                    <h4>{g.label()} <span>{ids.length}</span></h4>
                    <div className="names">
                      {ids.map((id) => g.key === 'wrong'
                        ? <span key={id} className="name locked" title={w('noCallWrong')}>{nameOf(id)}</span>
                        : <button key={id} className="name" onClick={() => callOn('any', id)}>{nameOf(id)}</button>)}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
          {!answerable && (
            <div className="names">
              {present.map((p) => <button key={p.id} className="name" onClick={() => callOn('any', p.id)}>{p.name}</button>)}
            </div>
          )}
        </section>
      </div>

      <Sheet open={ending} onClose={() => setEnding(false)} title={w('endLesson')}>
        <div className="center"><Pichi size={100} stage={3} mood="wow" /></div>
        <h2 className="center">{w('endLesson')}</h2>
        <p className="center">{w('confirmEnd')}</p>
        <div className="sheet-actions">
          <button className="big" onClick={() => setEnding(false)}>{w('cancel')}</button>
          <button className="big danger-fill" onClick={() => { setEnding(false); send({ t: 'end' }) }}><Icon name="check" /> {w('endLesson')}</button>
        </div>
      </Sheet>

      {tally && tally.reteach.length > 0 && (
        <section className="card reteach-card">
          <h2><Icon name="refresh" /> {w('needsReteach')} <span className="muted small">{w('reteachWhy')}</span></h2>
          <ul className="list">
            {tally.reteach.map((r) => {
              const here = present.some((p) => p.id === r.user_id)
              return (
                <li key={r.id}>
                  <span><strong>{r.name}</strong> <span className="muted small">· {r.lesson_id}</span></span>
                  <span className="row">
                    <button disabled={!here} onClick={() => callOn('any', r.user_id)}><Icon name="hand" size={16} /> {w('callOn')}</button>
                    <button disabled={!here || sent[r.id]} onClick={() => { send({ t: 'reteach', userId: r.user_id, stepIds: r.step_ids }); setSent((s) => ({ ...s, [r.id]: true })) }}>
                      <Icon name="refresh" size={16} /> {w('sendReview')}
                    </button>
                    <button onClick={() => void api.resolve(r.id)}><Icon name="check" size={16} /> {w('resolved')}</button>
                  </span>
                </li>
              )
            })}
          </ul>
        </section>
      )}
    </div>
  )
}
